import { createSelector } from "reselect";
import { getAllSites } from "./sites";

// Dashboard site filter keys, kept in state.generalSettings.dashboardFilters.
export const WITH_ALARMS = "withAlarms";
export const WITH_ONLINE = "withOnline";

const EMPTY_FILTERS = [];

const selectGateways = (state) => state.gateways;
const selectAlerts = (state) => state.alerts;
const selectIncludeHiddenSites = (state) =>
  state.generalSettings?.includeHiddenSites ?? false;
const selectIncludeEmptySites = (state) =>
  state.generalSettings?.includeEmptySites ?? true;
const selectDashboardFilters = (state) =>
  state.generalSettings?.dashboardFilters ?? EMPTY_FILTERS;

/**
 * Sites enriched for the dashboard. Each site is a new object (the store is never mutated):
 * - gateways: the site's gateways merged with their live store entry; gateways the store
 *   doesn't know yet are dropped.
 * - alarms: one alert bucket per gateway of the site that has active alerts.
 * - offlineGateways: the site's gateways whose status is "OFF".
 * - gatewayGuids: every gateway guid the backend lists for the site.
 */
export const getDashboardSites = createSelector(
  [getAllSites, selectGateways, selectAlerts],
  (sites, gateways, alerts) => {
    const gatewaysById = gateways?.byId ?? {};
    const alertsById = alerts?.byId ?? {};

    return sites.map((site) => {
      const siteGateways = site.gateways ?? [];
      const knownGateways = siteGateways
        .filter((gw) => gatewaysById[gw.gatewayGuid] !== undefined)
        .map((gw) => ({ ...gw, ...gatewaysById[gw.gatewayGuid] }));

      return {
        ...site,
        gatewayGuids: siteGateways.map((gw) => gw.gatewayGuid),
        gateways: knownGateways,
        alarms: siteGateways
          .filter((gw) => alertsById[gw.gatewayGuid] !== undefined)
          .map((gw) => alertsById[gw.gatewayGuid]),
        offlineGateways: knownGateways.filter((gw) => gw.status === "OFF"),
      };
    });
  }
);

// True when the site passes every dashboard filter (search is applied separately).
export const siteMatchesDashboardFilters = (site, settings) => {
  const { filters, includeHiddenSites, includeEmptySites } = settings;

  if (!includeHiddenSites && site.isHidden) return false;
  // Empty site: the backend lists no gateways for it.
  if (!includeEmptySites && site.gatewayGuids.length === 0) return false;

  for (const filterKey of filters) {
    switch (filterKey) {
      case WITH_ALARMS:
        if (!(site.offlineGateways.length > 0 || site.alarms.length > 0)) {
          return false;
        }
        break;
      case WITH_ONLINE:
        if (site.offlineGateways.length === site.gateways.length) {
          return false;
        }
        break;
      default:
        break;
    }
  }
  return true;
};

/**
 * Dashboard sites after the Filters menu (with issues, online, hidden, empty), before the
 * search box. The site list, the map, YOUR INSTALLATIONS and ACTIVE ALARMS all start from
 * this list, so those counts always match the filters.
 */
export const getFilteredDashboardSites = createSelector(
  [
    getDashboardSites,
    selectDashboardFilters,
    selectIncludeHiddenSites,
    selectIncludeEmptySites,
  ],
  (sites, filters, includeHiddenSites, includeEmptySites) =>
    sites.filter((site) =>
      siteMatchesDashboardFilters(site, {
        filters,
        includeHiddenSites,
        includeEmptySites,
      })
    )
);
